import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import type { SeriesPoint } from '~/api/types'
import { CHART_UI } from '~/features/monitoring/chart-colors'

type Bucket = { month: string; count: number }

/** 측정 시각들을 월(YYYY-MM) 버킷으로 묶어 횟수를 센다(오래된→최신). */
function bucketByMonth(series: SeriesPoint[]): Bucket[] {
    const counts = new Map<string, number>()
    for (const s of series) {
        const month = (s.measuredAt ?? '').slice(0, 7) // YYYY-MM
        if (month.length === 7) counts.set(month, (counts.get(month) ?? 0) + 1)
    }
    return [...counts.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, count]) => ({ month, count }))
}

function TipBox(props: { active?: boolean; payload?: Array<{ payload: Bucket }> }) {
    const { active, payload } = props
    if (!active || !payload || payload.length === 0) return null
    const p = payload[0].payload
    return (
        <div className='border-border bg-card rounded-md border px-3 py-2 text-xs shadow-md'>
            <p className='text-muted-foreground'>{p.month}</p>
            <p className='text-foreground mt-0.5 font-semibold'>{p.count}회 이행</p>
        </div>
    )
}

/**
 * 빈도/주기 지표 히스토그램 — 값 임계선이 아니라 기간별 '이행 횟수' 막대.
 * '연 1회 이상' 같은 주기 요건은 매 측정값이 아니라 기간 내 몇 번 이행했는지로 본다.
 */
export function FrequencyChart({ series }: { series: SeriesPoint[] }) {
    const data = bucketByMonth(series)
    if (data.length === 0) {
        return (
            <p className='text-muted-foreground text-xs'>
                이행 기록이 없어 히스토그램을 그릴 수 없어요.
            </p>
        )
    }
    return (
        <div className='h-72 w-full'>
            <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                    data={data}
                    margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid
                        strokeDasharray='3 3'
                        stroke={CHART_UI.grid}
                        vertical={false}
                    />
                    <XAxis
                        dataKey='month'
                        tick={{ fontSize: 11, fill: CHART_UI.axis }}
                        tickLine={false}
                        axisLine={{ stroke: CHART_UI.grid }}
                        minTickGap={12}
                    />
                    <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 11, fill: CHART_UI.axis }}
                        tickLine={false}
                        axisLine={false}
                        width={32}
                    />
                    <Tooltip
                        content={<TipBox />}
                        cursor={{ fill: CHART_UI.grid, fillOpacity: 0.3 }}
                    />
                    <Bar
                        dataKey='count'
                        fill={CHART_UI.line}
                        radius={[3, 3, 0, 0]}
                        maxBarSize={48}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}
